import { Box, Chip, Tooltip, CircularProgress, InputBase } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RestaurantMenu as MenusIcon,
  Add as AddIcon,
  BookmarkAddOutlined as SaveTemplateIcon,
  ContentCopyOutlined as DuplicateIcon,
  DeleteOutlineOutlined as DeleteIcon,
  PowerSettingsNew as ActivateIcon,
  SearchOutlined as SearchIcon,
} from "@mui/icons-material";
import Pagination from "../../ui/Pagination";
import { formatDateHebrew } from "../../../utils/formUtils";
import { v2, nums } from "./ui/v2Tokens";
import { macro, type MacroToken } from "./ui/macroTokens";
import { PrimaryButton, TextButton } from "./ui/primitives";
import CreateMenuDialog, { type CreateMenuChoice } from "./dialogs/CreateMenuDialog";
import SaveMenuAsTemplateDialog from "./dialogs/SaveMenuAsTemplateDialog";
import ConfirmDialog from "./dialogs/ConfirmDialog";
import {
  deleteMenu,
  duplicateMenu,
  saveMenuAsTemplate,
  setMenuActive,
} from "../../../services/NutritionV2Service";
import { useMenusV2 } from "./data/useNutritionV2";
import MenuTypeChip from "./ui/MenuTypeChip";
import {
  POINTS,
  POINTS_SHORT,
  energyFigure,
  energyLabel,
  macroFigure,
  pointsFromMacro,
  type NutritionDisplayMode,
  type PointsMacroKey,
} from "./ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../contexts/NutritionDisplayContext";
import {
  isMealBuilderType,
  menuMacros,
  isTraineeOwned,
  type MenuV2,
} from "./model/nutritionV2Model";

const f1 = (n: number) => Math.round(n * 10) / 10;

/**
 * A table cell's bare figure — the column header carries the unit, so the cell does not. `–` for a
 * menu with none of it. In points mode the header says נקודות and every figure under it is one.
 */
const energyCell = (kcal: number, mode: NutritionDisplayMode): string =>
  kcal ? energyFigure(kcal, mode).text : "–";

const macroCell = (key: PointsMacroKey, grams: number, mode: NutritionDisplayMode): string =>
  (mode === POINTS ? pointsFromMacro(key, grams) : f1(grams)) ? macroFigure(key, grams, mode).text : "–";

/** The column headers in the company's unit: `קלוריות` → `נקודות`, and the macros say they are points. */
const headersFor = (headers: string[], mode: NutritionDisplayMode): string[] =>
  headers.map((h) =>
    h === "קלוריות" ? energyLabel(mode) : h === "מקרו" && mode === POINTS ? `מקרו (${POINTS_SHORT})` : h
  );

/**
 * Nine columns come to roughly 950px of natural width, against the ~289px a phone has inside this
 * card. The wrapper scrolls, so nothing BREAKS — but three and a half screens of horizontal drag to
 * reach a row's own delete button is not a usable list.
 *
 * So below `md` the table shows what a trainer picks a menu BY — its name, what it comes to, whether
 * it is running, and what they can do with it — and hides the rest. `display: none` rather than not
 * rendering: the cells stay in the DOM in their original order, so nothing about the table's
 * structure changes with the viewport and the columns come back untouched on a wider window.
 */
const MOBILE_HIDDEN = { display: { xs: "none", md: "table-cell" } };

/** Column indices of `HEADERS` that stand down on a phone: type, days, macros, meals, created. */
const HIDDEN_ON_MOBILE = new Set([1, 2, 4, 5, 6]);

const HEADERS = ["שם התפריט", "סוג", "ימים", "קלוריות", "מקרו", "ארוחות", "נוצר", "סטטוס", ""];

const cell = {
  padding: { xs: "10px 8px", md: "12px 14px" },
  borderBottom: `1px solid ${v2.lineSoft}`,
  fontSize: "13px",
  color: v2.ink2,
  verticalAlign: "middle",
  textAlign: "center" as const,
  ...nums,
};

const th = {
  ...cell,
  padding: { xs: "8px 8px", md: "9px 14px" },
  fontSize: "11.5px",
  fontWeight: 500,
  color: v2.ink3,
  borderBottom: `1px solid ${v2.line}`,
  background: v2.canvas,
  whiteSpace: "nowrap" as const,
};

/** Row actions are icon-only — the label lives in the tooltip and the aria-label. */
const iconBtn = (color: string) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: { xs: "40px", md: "30px" },
  height: { xs: "40px", md: "30px" },
  border: "none",
  background: "transparent",
  borderRadius: "6px",
  color: v2.ink3,
  cursor: "pointer",
  fontFamily: "inherit",
  padding: 0,
  transition: "background .12s, color .12s",
  "&:hover": { color, background: v2.lineSoft },
});

/**
 * One macro as `ח׳ 203` — the abbreviation muted, the number in the macro's own hue.
 *
 * The column used to be three bare numbers split by dots, so "which one is protein" rested
 * entirely on colour and column order: unreadable to anyone who cannot separate the three
 * hues, and a guess for everyone else. Only the NUMBER stays coloured — tinting the letter
 * too made a three-macro cell read as six competing items.
 *
 * The letter comes first in the DOM so RTL lands it to the RIGHT of its number, matching the
 * `ח׳ 20.1` serving pills in the food dictionary. It is an `<abbr>` carrying the full word,
 * so hovering (or a screen reader) still resolves ח׳ to חלבון.
 */
const MacroShort = ({ token, macroKey, value }: { token: MacroToken; macroKey: PointsMacroKey; value: number }) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "baseline", gap: "3px", whiteSpace: "nowrap" }}
  >
    <Box
      component="abbr"
      title={token.label}
      sx={{ color: v2.ink3, fontWeight: 500, textDecoration: "none", cursor: "help" }}
    >
      {token.short}
    </Box>
    <Box component="span" sx={{ color: token.text }}>
      {macroCell(macroKey, value, useNutritionDisplayMode())}
    </Box>
  </Box>
);

const DAY_SHORT: Record<string, string> = {
  SUNDAY: "א׳",
  MONDAY: "ב׳",
  TUESDAY: "ג׳",
  WEDNESDAY: "ד׳",
  THURSDAY: "ה׳",
  FRIDAY: "ו׳",
  SATURDAY: "ש׳",
};

const formatDays = (days: string[]): string => {
  if (!days || days.length === 0) return "לא מוגדר";
  if (days.length === 7) return "כל השבוע";
  return days.map((d) => DAY_SHORT[d] || d).join(", ");
};

/**
 * Rows per page, fixed — the same shape as the food dictionary (`FoodDictionaryScreen`), which is
 * this app's reference list screen: a module constant and no rows-per-page selector, so every list
 * pages the same way and there is one fewer control to explain.
 */
const PAGE_SIZE = 8;

/**
 * The toolbar's status filter, in the scheduled-notifications page's shape (`STATUS_FILTERS`
 * there) so the two screens read the same: `null` is "no filter", not a third state.
 *
 * A menu is active or paused and nothing else, so the notifications page's three chips map over
 * without inventing a category. `active` is what the table's first sort key already groups by —
 * the chips let a trainer keep one of those groups instead of scrolling past the other.
 */
const STATUS_FILTERS: Array<{ value: boolean | null; label: string }> = [
  { value: null, label: "הכל" },
  { value: true, label: "פעיל" },
  { value: false, label: "לא פעיל" },
];

/**
 * When the menu was created, in ms — or 0 for one the server sent no date for.
 *
 * `Date.parse` answers NaN for a missing or malformed value, and a NaN in a comparator does not
 * merely misplace one row: it makes the comparison intransitive and can scramble the whole list.
 * Undated menus sort oldest, which is where an unknown creation date belongs.
 */
const createdMs = (menu: MenuV2): number => {
  const ms = Date.parse(menu.createdDate ?? "");
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Active menus first, newest first within each half.
 *
 * <p>Before this the list rendered the server's order verbatim — `createdDate DESC` and nothing
 * else — so a menu paused three weeks ago sat above the one the trainee is eating from today. What
 * a trainer opens this tab to answer is "what is running right now"; the history is the second
 * question, and it is still there, one group down.
 *
 * <p>Several menus can be active at once ON PURPOSE (see `MenuV2.active`), so "active" is a group
 * of rows rather than a single row to pin — hence a sort rather than a hoist.
 */
export const byActiveThenNewest = (a: MenuV2, b: MenuV2): number =>
  Number(b.active) - Number(a.active) || createdMs(b) - createdMs(a);

/** Case-insensitive because a trainer types "PDF" or a brand name in Latin; Hebrew has no case. */
const matchesSearch = (menu: MenuV2, term: string): boolean =>
  menu.name.toLowerCase().includes(term);

const MenusListV2 = ({ traineeId }: { traineeId: string }) => {
  const navigate = useNavigate();
  const { data: menus, loading, error, reload } = useMenusV2(traineeId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const units = useNutritionDisplayMode();
  const [savingTemplate, setSavingTemplate] = useState<MenuV2 | null>(null);
  const [deleting, setDeleting] = useState<MenuV2 | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<boolean | null>(null);
  const [page, setPage] = useState(0);

  const term = search.trim().toLowerCase();
  /** Any narrowing at all — the count, the empty state and the "clear" affordance all key off it. */
  const filtering = term.length > 0 || status !== null;

  // Search, status, sort and paging are all done HERE, unlike the food dictionary, which pages on
  // the server. Not a divergence of style but of data: `listMenus` fetches every menu a trainee has
  // in ONE request with no page parameters to pass, so the whole list is already in the browser. A
  // server endpoint would buy nothing and cost a round trip per keystroke.
  //
  // The two filters are AND-ed on purpose: the chips narrow the same list the search box does, so
  // "פעיל" + "keto" means the active menu called keto rather than either of them.
  const visible = useMemo(
    () =>
      menus
        .filter((menu) => status === null || menu.active === status)
        .filter((menu) => !term || matchesSearch(menu, term))
        .sort(byActiveThenNewest),
    [menus, term, status]
  );

  const totalPages = Math.ceil(visible.length / PAGE_SIZE);
  // Deleting the last row of the last page leaves `page` past the end, which would render an empty
  // table for a list that has rows. Clamping (rather than resetting to 0) keeps the trainer where
  // they were, on the page that now ends the list.
  const currentPage = Math.max(0, Math.min(page, totalPages - 1));
  const rows = visible.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  /** A new search is a new list — page 3 of the old results means nothing in it. */
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  /** Same reasoning as the search: a narrower list is a different list, so it starts at page 1. */
  const handleStatus = (value: boolean | null) => {
    setStatus(value);
    setPage(0);
  };

  /** The way out of an empty result — drops BOTH filters, whichever of them emptied the table. */
  const clearFilters = () => {
    setSearch("");
    setStatus(null);
    setPage(0);
  };

  // Named for what the trainer actually did: a search with no hits reads back the name they typed,
  // a status chip with no hits reads back the status. With neither filter on, `visible` is `menus`
  // and this line is unreachable — a non-empty list cannot filter down to nothing through no filter.
  const statusAdjective = status === null ? "" : status ? "פעיל" : "לא פעיל";
  const noResultsHint = term
    ? `אין תפריט ${statusAdjective ? `${statusAdjective} ` : ""}בשם "${search.trim()}"`
    : `אין תפריטים ${status ? "פעילים" : "לא פעילים"}`;

  /**
   * Every mutation here is followed by a reload rather than a local edit of `menus`. The server
   * decides things the client cannot — a duplicate's name, whether a save changed the note's date —
   * so patching the row in place would show the trainer a menu that differs from the stored one.
   */
  const afterChange = () => reload();

  const startCreate = (choice: CreateMenuChoice) => {
    setDialogOpen(false);
    const query = choice.templateId ? `?templateId=${choice.templateId}` : "";
    // The CANONICAL path, not the legacy `/nutrition-v2` one. That one only redirects here, and
    // going through the hop is what lost `?templateId=` — the redirect rebuilds the URL from the
    // route params. It carries the query now too, but there is no reason to take the detour.
    navigate(`/app/clients/${traineeId}/nutrition/menu/new${query}`);
  };

  return (
    <>
      <CreateMenuDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreate={startCreate} />

      <SaveMenuAsTemplateDialog
        open={Boolean(savingTemplate)}
        onClose={() => setSavingTemplate(null)}
        defaultName={savingTemplate?.name || ""}
        mealCount={savingTemplate?.meals.length || 0}
        onSave={(name) => {
          if (savingTemplate) void saveMenuAsTemplate(name, savingTemplate);
          setSavingTemplate(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="למחוק את התפריט?"
        message={`"${deleting?.name}" יימחק לצמיתות, על כל הארוחות שבו. אי אפשר לבטל את הפעולה.`}
        confirmLabel="מחק תפריט"
        onConfirm={() => {
          if (deleting) void deleteMenu(deleting.id).then(afterChange);
          setDeleting(null);
        }}
      />

      {/*
        The page's own action, ABOVE the card rather than inside its header — the shape the
        scheduled-notifications page uses (`NotificationsPage`), which this screen now follows.

        That page pairs the button with a title and subtitle on the right. There is deliberately
        no title here: this list is a TAB inside the client's nutrition page, which already renders
        "תזונה" as the page heading and "תפריטים" as the selected tab directly above. A third
        heading saying the same thing a third time is chrome, not information.
      */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: "18px" }}>
        <PrimaryButton onClick={() => setDialogOpen(true)}>
          <AddIcon sx={{ fontSize: 16 }} />
          תפריט חדש
        </PrimaryButton>
      </Box>

      <Box
        sx={{
          background: v2.surface,
          border: `1px solid ${v2.line}`,
          borderRadius: v2.rLg,
          boxShadow: v2.shadow,
          overflow: "hidden",
        }}
      >
        {/*
          The filter bar, in the notifications page's order: count pill, then search, then the
          status chips, then a spacer that pushes nothing to the far side. The search used to float
          in the middle of a header that also carried a title and the create button, which is
          exactly what made trainers ask what it was doing there.
        */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            flexWrap: "wrap",
            padding: "10px 14px",
            borderBottom: `1px solid ${v2.line}`,
            background: v2.canvas,
          }}
        >
          {/* The count pill. While filtering, the bare total would contradict the rows on screen —
              "7 תפריטים" above a single result reads as a bug. Say both numbers instead. */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
              fontWeight: 700,
              color: v2.brandText,
              background: v2.brandWash,
              border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px",
              borderRadius: "20px",
              whiteSpace: "nowrap",
            }}
          >
            <MenusIcon sx={{ fontSize: 14 }} />
            {filtering ? `${visible.length} מתוך ${menus.length} תפריטים` : `${menus.length} תפריטים`}
          </Box>

          {/*
            The food dictionary's search box, in V2's colours: an InputBase with the magnifier
            absolutely positioned on the left and the padding to clear it, the same focus ring, and
            the same "…" placeholder. Rendered only once there is something to search — a box that
            can only ever return nothing is worse than no box.

            Deliberately NOT debounced, which is the one place this parts company with the food
            dictionary. The 300ms there throttles a REQUEST per keystroke; there is no request here,
            so a debounce would only add lag between typing and seeing the rows change.
          */}
          {menus.length > 0 && (
            <Box sx={{ position: "relative" }}>
              <SearchIcon
                sx={{
                  position: "absolute",
                  left: 9,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 14,
                  color: v2.ink4,
                  pointerEvents: "none",
                }}
              />
              <InputBase
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") handleSearch("");
                }}
                placeholder="חיפוש לפי שם תפריט..."
                inputProps={{ "aria-label": "חיפוש תפריט", autoComplete: "off" }}
                sx={{
                  // fixed at 200 it neither grew nor shrank; on a phone the filter bar wraps and
                  // this simply takes the line it lands on
                  width: { xs: "100%", md: 200 },
                  p: "6px 10px 6px 30px",
                  border: `1.5px solid ${v2.line}`,
                  borderRadius: v2.rSm,
                  background: v2.surface,
                  fontSize: "12px",
                  "& input": { padding: 0 },
                  "& input::placeholder": { color: v2.ink4, opacity: 1 },
                  "&:focus-within": {
                    borderColor: v2.brand,
                    boxShadow: "0 0 0 3px rgba(1,187,167,.08)",
                  },
                }}
              />
            </Box>
          )}

          {/* Hidden alongside the search for the same reason: with no menus there is nothing to
              narrow, and three chips over an empty table are three dead controls.

              `aria-pressed` is not on the notifications page's chips. It is here because which chip
              is selected is otherwise carried by fill colour alone — the same objection this file
              already answers in the מקרו column. */}
          {menus.length > 0 && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {STATUS_FILTERS.map((sf) => {
                const selected = status === sf.value;
                return (
                  <Chip
                    key={String(sf.value)}
                    label={sf.label}
                    onClick={() => handleStatus(sf.value)}
                    aria-pressed={selected}
                    sx={{
                      fontWeight: 600,
                      fontSize: "13px",
                      height: "32px",
                      backgroundColor: selected ? v2.brand : v2.brandWash,
                      color: selected ? "#fff" : v2.ink2,
                      "&:hover": { backgroundColor: selected ? v2.brandDark : v2.line },
                    }}
                  />
                );
              })}
            </Box>
          )}

          <Box sx={{ flex: 1 }} />
        </Box>

        {/*
          Three states, not two. "Still loading" and "has none" look identical if only the array
          length is checked, so a trainer with a dozen menus is told they have none for as long as
          the request takes — and a failed request would say the same thing, permanently.
        */}
        {loading ? (
          <Box
            data-testid="menus-loading"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              gap: 1.5,
              padding: "44px",
            }}
          >
            <CircularProgress size={26} sx={{ color: v2.brand }} />
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>טוען תפריטים…</Box>
          </Box>
        ) : error ? (
          <Box
            data-testid="menus-error"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              gap: 1,
              padding: "44px",
            }}
          >
            <Box sx={{ fontSize: 15, fontWeight: 500, color: v2.over }}>לא הצלחנו לטעון את התפריטים</Box>
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>בדוק את החיבור ונסה שוב</Box>
            <TextButton onClick={reload}>נסה שוב</TextButton>
          </Box>
        ) : menus.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              gap: 1,
              padding: "44px",
            }}
          >
            <MenusIcon sx={{ fontSize: 46, color: v2.line }} />
            <Box sx={{ fontSize: 15, fontWeight: 500, color: v2.ink3 }}>אין תפריטי תזונה</Box>
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>צור תפריט תזונה ראשון עבור הלקוח</Box>
          </Box>
        ) : visible.length === 0 ? (
          /* A filter that matched nothing is NOT "this trainee has no menus" — saying so would
             tell a trainer their client's plans are gone. Different words, and a way back.

             The second line names whichever filter emptied the table, so "clear" has something to
             point at: a status chip on its own would otherwise leave the trainer reading an empty
             table under a search box they never typed in. */
          <Box
            data-testid="menus-no-results"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              gap: 1,
              padding: "44px",
            }}
          >
            <MenusIcon sx={{ fontSize: 46, color: v2.line }} />
            <Box sx={{ fontSize: 15, fontWeight: 500, color: v2.ink3 }}>לא נמצאו תפריטים</Box>
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>{noResultsHint}</Box>
            <TextButton onClick={clearFilters}>
              {status === null ? "נקה חיפוש" : "נקה סינון"}
            </TextButton>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead">
                <Box component="tr">
                  {headersFor(HEADERS, units).map((h, i) => (
                    <Box
                      component="th"
                      key={`${h}-${i}`}
                      sx={HIDDEN_ON_MOBILE.has(i) ? { ...th, ...MOBILE_HIDDEN } : th}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map((menu) => {
                  // what the trainee actually gets: the meals' sum for a detailed menu,
                  // the trainer's own numbers for a macro / PDF one. NOT the calculator.
                  const real = menuMacros(menu);
                  const showsTargetToo =
                    isMealBuilderType(menu.menuType) &&
                    // compared as shown: in points, 1,640 and 1,644 kcal are both 16.4 and the
                    // target line would only repeat the figure above it
                    energyFigure(menu.targets.calories, units).text !== energyFigure(real.calories, units).text;
                  return (
                    <Box
                      component="tr"
                      key={menu.id}
                      onClick={() => navigate(`/app/clients/${traineeId}/nutrition-v2/menu/${menu.id}`)}
                      sx={{ cursor: "pointer", transition: "background .12s", "&:hover": { background: v2.hover } }}
                    >
                      <Box component="td" sx={{ ...cell, fontWeight: 700, color: v2.ink, textAlign: "start" }}>
                        {menu.name}
                        {/* A menu the trainee wrote for themselves. The trainer needs to KNOW
                            it exists — a trainee quietly running their own numbers is the
                            thing a trainer most needs to see — but must not be able to
                            change it, so the row's write actions are withheld below. */}
                        {isTraineeOwned(menu) && (
                          <Box
                            sx={{
                              display: "inline-block",
                              marginInlineStart: "8px",
                              fontSize: "11px",
                              fontWeight: 500,
                              padding: "1px 7px",
                              borderRadius: "5px",
                              background: "#F3F0FF",
                              color: "#5B4BA8",
                            }}
                          >
                            נכתב על ידי המתאמן
                          </Box>
                        )}
                      </Box>
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN }}>
                        <MenuTypeChip menuType={menu.menuType} />
                      </Box>
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN, color: v2.ink2 }}>
                        {formatDays(menu.applicableDays)}
                      </Box>
                      <Box component="td" sx={{ ...cell, fontWeight: 700 }}>
                        {energyCell(real.calories, units)}
                        {showsTargetToo && (
                          <Box sx={{ fontSize: 10.5, fontWeight: 600, color: v2.ink3 }}>
                            יעד {energyFigure(menu.targets.calories, units).text}
                          </Box>
                        )}
                      </Box>
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN }}>
                        {/* gap is wider than the letter-to-number gap inside each pair, so the
                            three read as three units rather than six loose tokens */}
                        <Box sx={{ display: "inline-flex", gap: "7px", fontSize: 11.5, fontWeight: 600 }}>
                          <MacroShort token={macro.protein} macroKey="protein" value={real.protein} />
                          <Box component="span" sx={{ color: v2.ink4 }}>·</Box>
                          <MacroShort token={macro.carbs} macroKey="carbs" value={real.carbs} />
                          <Box component="span" sx={{ color: v2.ink4 }}>·</Box>
                          <MacroShort token={macro.fats} macroKey="fats" value={real.fats} />
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN, color: v2.ink2 }}>
                        {isMealBuilderType(menu.menuType) ? `${menu.meals.length} ארוחות` : "—"}
                      </Box>
                      {/* The date the sort's second key reads from, so the order is legible rather
                          than something the trainer has to take on trust. `formatDateHebrew` is the
                          app's shared formatter — it is careful about timezones, and createdDate is
                          a full ISO instant, so the day shown is the viewer's local day. */}
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN, color: v2.ink3, whiteSpace: "nowrap" }}>
                        {formatDateHebrew(menu.createdDate) || "—"}
                      </Box>
                      {/* Status READS, it does not act. It used to be the toggle itself, styled
                          exactly like the read-only status chips everywhere else in the app and
                          sitting under a column headed "סטטוס" — so nobody could tell it was a
                          button, and trainers reported there was no way to pause a menu at all.
                          The switch now lives with the other row actions, where a control belongs. */}
                      <Box
                        component="td"
                        sx={{
                          ...cell,
                          fontWeight: 600,
                          color: menu.active ? v2.brandText : v2.over,
                        }}
                      >
                        {menu.active ? "פעיל" : "לא פעיל"}
                      </Box>
                      <Box component="td" sx={cell} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                          {isTraineeOwned(menu) ? (
                            <Box sx={{ fontSize: "12px", color: v2.ink4 }}>לקריאה בלבד</Box>
                          ) : (
                            <>
                          <Tooltip title={menu.active ? "השהה תפריט" : "הפעל תפריט"} arrow>
                            <Box
                              component="button"
                              type="button"
                              aria-label={`${menu.active ? "השהה" : "הפעל"} את ${menu.name}`}
                              aria-pressed={menu.active}
                              onClick={() => void setMenuActive(menu.id, !menu.active).then(afterChange)}
                              sx={iconBtn(menu.active ? v2.brand : v2.ink4)}
                            >
                              <ActivateIcon sx={{ fontSize: 17 }} />
                            </Box>
                          </Tooltip>
                          <Tooltip title="שמור כתבנית" arrow>
                            <Box
                              component="button"
                              type="button"
                              aria-label={`שמור ${menu.name} כתבנית`}
                              onClick={() => setSavingTemplate(menu)}
                              sx={iconBtn(v2.ink2)}
                            >
                              <SaveTemplateIcon sx={{ fontSize: 17 }} />
                            </Box>
                          </Tooltip>
                          <Tooltip title="שכפל" arrow>
                            <Box
                              component="button"
                              type="button"
                              aria-label={`שכפל ${menu.name}`}
                              onClick={() => void duplicateMenu(menu.id).then(afterChange)}
                              sx={iconBtn(v2.ink2)}
                            >
                              <DuplicateIcon sx={{ fontSize: 16 }} />
                            </Box>
                          </Tooltip>
                          <Tooltip title="מחק" arrow>
                            <Box
                              component="button"
                              type="button"
                              aria-label={`מחק ${menu.name}`}
                              onClick={() => setDeleting(menu)}
                              sx={iconBtn(v2.over)}
                            >
                              <DeleteIcon sx={{ fontSize: 17 }} />
                            </Box>
                          </Tooltip>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}

        {/* The app's shared pager, called exactly as the food dictionary calls it: no
            `onItemsPerPageChange`, so no rows-per-page selector, and hidden below two pages. */}
        {!loading && !error && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={visible.length}
            itemsPerPage={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </Box>
    </>
  );
};

export default MenusListV2;
